import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { forgetAgentFeatures } from '@/workspace/agent-features';
import { acceptCompanyInvitation, fetchCompany, type CompanyContext } from './api';
import { CompanyDocumentTitle } from './document-title';
import { useAppCatalogLocale } from '@/i18n/context';
import companyErrors from '@/i18n/ns/company-errors';
import { useNamespaceCopy } from '@/i18n/ns';

const Context = createContext<{
  ready: boolean;
  data: CompanyContext | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  adopt: (data: CompanyContext) => void;
}>({
  ready: false,
  data: null,
  loading: false,
  error: null,
  refresh: async () => {},
  adopt: () => {},
});

export const useCompany = () => useContext(Context);

// Whose company the rollout flags were last read for (see CompanyProvider).
let flagsOwner: string | undefined;

export function CompanyProvider({
  userId,
  inviteToken,
  onInviteAccepted,
  children,
}: {
  userId?: string;
  inviteToken?: string | null;
  onInviteAccepted?: () => void;
  children: ReactNode;
}) {
  const [data, setData] = useState<CompanyContext | null>(null);
  const [dataUserId, setDataUserId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(Boolean(userId));
  // Retain the failure state, not the language from the request's start.
  // The visible error also follows a language change after the request settles.
  const [failed, setFailed] = useState(false);
  const locale = useAppCatalogLocale();
  const copy = useNamespaceCopy(companyErrors);
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setFailed(false);
    try {
      setData(
        await (inviteToken ? acceptCompanyInvitation(inviteToken, locale) : fetchCompany(locale)),
      );
      setDataUserId(userId);
      if (inviteToken) onInviteAccepted?.();
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [userId, inviteToken, locale]);
  // Rollout flags such as Storefront belong to the signed-in account's company. The app remounts
  // this provider for each account, so the owner lives at module scope, and the flags are forgotten
  // while rendering: the shell must not show the previous company's flags while this one loads.
  if (flagsOwner !== userId) {
    flagsOwner = userId;
    forgetAgentFeatures();
  }
  useEffect(() => {
    let active = true;
    setData(null);
    setFailed(false);
    setLoading(Boolean(userId));
    if (userId)
      void (
        inviteToken
          ? acceptCompanyInvitation(inviteToken, localeRef.current)
          : fetchCompany(localeRef.current)
      )
        .then((value) => {
          if (active) {
            setData(value);
            setDataUserId(userId);
            if (inviteToken) onInviteAccepted?.();
          }
        })
        .catch(() => {
          if (active) setFailed(true);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    return () => {
      active = false;
    };
  }, [userId, inviteToken]);
  return (
    <Context.Provider
      value={{
        ready: Boolean(
          dataUserId === userId &&
          data?.company &&
          data.membership &&
          !data.onboarding.required &&
          !loading &&
          !failed,
        ),
        data: dataUserId === userId ? data : null,
        loading: Boolean(userId) && loading,
        error: userId && failed ? copy.loadFailed : null,
        refresh,
        adopt: (value) => {
          setData(value);
          setDataUserId(userId);
        },
      }}
    >
      <CompanyDocumentTitle name={dataUserId === userId ? data?.company?.name : undefined} />
      {children}
    </Context.Provider>
  );
}

export function CompanyChatSurface({
  children,
  signedIn,
}: {
  children: ReactNode;
  signedIn: boolean;
}) {
  const { data, loading, error } = useCompany();
  const welcomeOpen =
    !signedIn ||
    loading ||
    Boolean(error) ||
    !data ||
    Boolean(data.onboarding.required) ||
    !data.company;
  return (
    <div className={welcomeOpen ? 'hidden' : 'flex min-w-0 flex-1'} inert={welcomeOpen}>
      {children}
    </div>
  );
}
